import { confirmInApp } from "../common/AppDialog";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import { useMobile } from "../../hooks/useMobile";
import { useVoiceToText } from "../../hooks/useVoiceToText";
import DictationLangToggle from "../common/DictationLangToggle";
import { dictationText } from "../../utils/dictationUtils";
import {
  toSortedArray,
  quickAddTask,
  appendDailySpark,
  appendDailyLogEntry,
  toggleTaskLine,
  deleteText,
  reorderTexts,
  reorderLineInText,
  migrateDiarioLine,
  captureToCategory,
  resolveCategoryPaths,
  polishNoteAfterSave,
} from "../../services/workspaceService";
import { smartPaste, isPasteDump } from "../../utils/normalizePaste";
import { validateVoiceFile, transcribeAudioFile } from "../../services/deepgramTranscribe";
import {
  resolveCodeProjects,
  getCodeProject,
  loadCodeProjectId,
  saveCodeProjectId,
  touchRecentCodeProject,
} from "../../config/codeProjects";
import CodeProjectsEditor from "../code-projects/CodeProjectsEditor";
import HoyCategoryBar from "./HoyCategoryBar";
import ReadingQueueBanner from "../reading/ReadingQueueBanner";
import { textReaderPath, navigateToText, categoryPathLabel, findTextInWorkspace, HOME_PATH } from "../../utils/textRoutes";
import { TYPE_TO_SYM, detectEntryType, parseDailyLog, isDiarioNote } from "../../utils/bujo";
import { SymbolStrip, EntryIcon, GlyphMigrate } from "../today/BujoGlyphs";
import HintTip from "../common/HintTip";
import UnlockSeal from "../common/UnlockSeal";
import QuickMoveBar from "../common/QuickMoveBar";
import {
  CheckSquare,
  Square,
  Trash2,
  Plus,
  GripVertical,
  Sparkles,
  PenLine,
  BookOpen,
  HelpCircle,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader2,
  ClipboardPaste,
  ClipboardCopy,
  Mic,
  MicOff,
  Maximize2,
  ListFilter,
} from "lucide-react";
import AgentBotIcon from "../common/AgentBotIcon";
import BackgroundImage from "../common/BackgroundImage";
import { BACKGROUNDS } from "../../config/backgrounds";
import deskGlass, { ACCENT } from "../../styles/deskGlass";
import { runFeedbackFlow } from "../../utils/runFeedbackFlow";

const styles = deskGlass;

function SafeDroppable({ children, ...props }) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(id);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
}

function HelpBlock({ title, children }) {
  return (
    <section style={{ marginBottom: "16px" }}>
      <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: ACCENT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {title}
      </h3>
      <ul style={{ margin: 0, paddingLeft: "18px", ...styles.subtext, lineHeight: 1.65, fontSize: "0.85rem" }}>
        {children}
      </ul>
    </section>
  );
}

function GlassSection({ title, icon: Icon, headerExtra, children, style }) {
  return (
    <section style={{ ...styles.glassSection, marginBottom: "12px", ...style }}>
      {(title || Icon || headerExtra) && (
        <div style={{ ...styles.glassHeader, justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flexShrink: 0 }}>
            {Icon && <Icon size={18} color="#fff" />}
            {title && <h2 style={{ ...styles.glassTitle, margin: 0 }}>{title}</h2>}
          </div>
          {headerExtra && (
            <div style={{ minWidth: 0, flex: 1, textAlign: "right" }}>{headerExtra}</div>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

function getDailyLogNotebook(workspace, settings) {
  if (!workspace || !settings?.dailyLogPath) return null;
  const { deskId, drawerId, notebookId } = settings.dailyLogPath;
  const desk = workspace.desks?.find((d) => d.id === deskId);
  if (!desk) return null;
  const drawer = toSortedArray(desk.drawers).find((dr) => dr.id === drawerId);
  if (!drawer) return null;
  const notebook = toSortedArray(drawer.notebooks).find((n) => n.id === notebookId);
  return notebook || null;
}

function isToday(ts) {
  if (!ts) return false;
  const d = new Date(ts);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}

function plainPreview(content, max = 140) {
  if (!content) return "";
  const text = content
    .replace(/<[^>]*>/g, " ")
    .replace(/\[([ xX])\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function parseTasks(content) {
  if (!content || typeof content !== "string") return [];
  const lines = content.split("\n");
  const tasks = [];
  lines.forEach((line, idx) => {
    const match = line.match(/^(\s*)-\s*\[([ xX])\]\s*(.*)$/);
    if (match) {
      tasks.push({
        lineIndex: idx,
        indent: match[1],
        done: match[2].toLowerCase() === "x",
        text: match[3].trim(),
        rawLine: line,
      });
    } else if (line.includes("[ ]") || line.includes("[x]")) {
      const m = line.match(/\[([ xX])\](.*)/);
      if (m) {
        tasks.push({
          lineIndex: idx,
          indent: "",
          done: m[1].toLowerCase() === "x",
          text: m[2].trim(),
          rawLine: line,
        });
      }
    }
  });
  return tasks;
}

function parseSparks(content) {
  if (!content) return [];
  return content
    .split("\n")
    .filter((l) => l.trim().startsWith("•"))
    .map((l) => l.replace(/^•\s*/, "").trim())
    .filter(Boolean);
}

function isSparkNote(title) {
  return (title || "").startsWith("Chispas ·");
}

const AGENT_PROMPT_KEY = "hoyDesk.agentPrompt";
const AGENT_PANEL_KEY = "hoyDesk.agentPanelOpen";
const AGENT_AUTO_KEY = "hoyDesk.agentAutoMode";
const CAPTURE_DEFAULT_PH = "Escribí acá y Enter. Pegá CLI, LLM web o código";
const AGENT_ROBOT_PH = "Instrucciones fijas (ej. laconic). Copiar las junta con la captura de abajo. Ctrl+A solo el campo activo.";
const CAPTURE_ROBOT_PH = "Mensaje para el agente. Copiar envía instrucciones + esto. Ctrl+A solo este campo.";
const AGENT_AUTO_PH = "Instrucciones que se repiten en cada Copiar. Abajo se limpia solo tras copiar.";
const CAPTURE_AUTO_PH = "Prompt del turno: escribí o pegá. Copiar manda todo al agente y vacía este campo.";
const ROBOT_DOUBLE_TAP_MS = 320;

function readAgentPrompt() {
  try {
    return localStorage.getItem(AGENT_PROMPT_KEY) || "";
  } catch {
    return "";
  }
}

function readAgentPanelOpen() {
  try {
    return localStorage.getItem(AGENT_PANEL_KEY) === "1" || readAgentAutoMode();
  } catch {
    return false;
  }
}

function readAgentAutoMode() {
  try {
    return localStorage.getItem(AGENT_AUTO_KEY) === "1";
  } catch {
    return false;
  }
}

function buildCaptureCopyText(agentPrompt, mainInput) {
  const agent = String(agentPrompt ?? "").trim();
  const main = String(mainInput ?? "");
  if (agent && main) return `${agent}\n\n${main}`;
  return agent || main;
}

function codeCaptureOpts(projectId, settings) {
  const project = getCodeProject(projectId, settings);
  if (!project) return {};
  return { notebookName: project.notebookName, color: project.color };
}

export default function Today() {
  const { workspace, settings, loading: workspaceLoading } = useWorkspace();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [input, setInput] = useState("");
  const [agentPrompt, setAgentPrompt] = useState(readAgentPrompt);
  const [agentPanelOpen, setAgentPanelOpen] = useState(readAgentPanelOpen);
  const [agentAutoMode, setAgentAutoMode] = useState(readAgentAutoMode);
  const [asTask, setAsTask] = useState(false);
  const [saveReceipt, setSaveReceipt] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeGlyph, setActiveGlyph] = useState(null);
  const [bujoOpen, setBujoOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeCodeProject, setActiveCodeProject] = useState(loadCodeProjectId);
  const [helpOpen, setHelpOpen] = useState(false);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [codeEditorOpen, setCodeEditorOpen] = useState(false);
  const [captureOptsOpen, setCaptureOptsOpen] = useState(false);
  const [captureFullscreenVisible, setCaptureFullscreenVisible] = useState(false);
  const [captureFullscreenAnim, setCaptureFullscreenAnim] = useState(false);
  const [captureInterim, setCaptureInterim] = useState("");
  const [dictationLang, setDictationLang] = useState("es");
  const codeProjects = useMemo(() => resolveCodeProjects(settings), [settings]);
  const receiptRef = useRef(null);
  const textareaRef = useRef(null);
  const agentPromptRef = useRef(null);
  const robotClickRef = useRef({ lastAt: 0, singleTimer: null });
  const [pasteBusy, setPasteBusy] = useState(false);
  const [isAudioDragOver, setIsAudioDragOver] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const audioAbortRef = useRef(null);

  const {
    isRecording: isCaptureRecording,
    startRecording: startCaptureRecording,
    stopRecording: stopCaptureRecording,
    error: captureVoiceError,
  } = useVoiceToText(
    (finalPayload) => {
      const text = dictationText(finalPayload);
      setCaptureInterim("");
      if (!text) return;
      setInput((prev) => {
        const p = String(prev ?? "");
        const glue = p && !p.endsWith(" ") ? " " : "";
        return `${p}${glue}${text}`;
      });
    },
    (interim) => setCaptureInterim(dictationText(interim)),
    { language: dictationLang },
  );

  useEffect(() => {
    if (!captureFullscreenVisible && isCaptureRecording) {
      stopCaptureRecording();
    }
  }, [captureFullscreenVisible, isCaptureRecording, stopCaptureRecording]);

  const openCaptureFullscreen = useCallback(() => {
    if (locked) return;
    setCaptureFullscreenVisible(true);
    window.setTimeout(() => setCaptureFullscreenAnim(true), 0);
  }, [locked]);

  const closeCaptureFullscreen = useCallback(() => {
    setCaptureFullscreenAnim(false);
    window.setTimeout(() => {
      setCaptureFullscreenVisible(false);
      setCaptureInterim("");
    }, 180);
  }, []);

  useEffect(() => {
    if (!captureFullscreenVisible) return;
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [captureFullscreenVisible]);

  const captureCopyText = useMemo(
    () => buildCaptureCopyText(agentPrompt, input),
    [agentPrompt, input],
  );

  const mainCapturePlaceholder = useMemo(() => {
    if (agentAutoMode && agentPanelOpen) return CAPTURE_AUTO_PH;
    if (agentPanelOpen) return CAPTURE_ROBOT_PH;
    return CAPTURE_DEFAULT_PH;
  }, [agentAutoMode, agentPanelOpen]);

  const agentFieldPlaceholder = agentAutoMode ? AGENT_AUTO_PH : AGENT_ROBOT_PH;

  const robotModeTitle = useMemo(() => {
    if (agentAutoMode) return "Auto agente: copiar limpia captura · doble toque = modo normal";
    if (agentPanelOpen) return "Modo agente · doble toque = auto loop";
    return "Modo agente · doble toque = auto loop";
  }, [agentAutoMode, agentPanelOpen]);

  const handleCopyCapture = useCallback(async () => {
    if (!captureCopyText) return;
    try {
      await navigator.clipboard.writeText(captureCopyText);
      if (agentAutoMode) {
        setInput("");
        window.setTimeout(() => textareaRef.current?.focus(), 0);
      }
    } catch (e) {
      console.warn("[HoyDesk] clipboard.writeText failed:", e);
      setSaveError("No se pudo copiar.");
      setTimeout(() => setSaveError(null), 2000);
    }
  }, [captureCopyText, agentAutoMode]);

  const handleRobotModeClick = useCallback(() => {
    if (locked) return;
    const now = Date.now();
    const prev = robotClickRef.current.lastAt;
    robotClickRef.current.lastAt = now;

    if (now - prev < ROBOT_DOUBLE_TAP_MS) {
      if (robotClickRef.current.singleTimer) {
        clearTimeout(robotClickRef.current.singleTimer);
        robotClickRef.current.singleTimer = null;
      }
      setAgentPanelOpen(true);
      setAgentAutoMode((auto) => {
        const next = !auto;
        window.setTimeout(() => {
          (next ? textareaRef : agentPromptRef).current?.focus();
        }, 0);
        return next;
      });
      return;
    }

    robotClickRef.current.singleTimer = window.setTimeout(() => {
      robotClickRef.current.singleTimer = null;
      setAgentPanelOpen((open) => {
        const next = !open;
        if (!next) setAgentAutoMode(false);
        else window.setTimeout(() => agentPromptRef.current?.focus(), 0);
        return next;
      });
    }, ROBOT_DOUBLE_TAP_MS);
  }, [locked]);

  const handlePasteButton = useCallback(async () => {
    if (locked || pasteBusy) return;
    setPasteBusy(true);
    try {
      const pasted = await navigator.clipboard.readText();
      if (!pasted) return;

      const clean = smartPaste(pasted);
      const el = textareaRef.current;

      setInput((prev) => {
        const start = el?.selectionStart ?? prev.length;
        const end = el?.selectionEnd ?? prev.length;
        const merged = prev.slice(0, start) + clean + prev.slice(end);
        return smartPaste(merged);
      });

      setSaveError(null);
      window.setTimeout(() => el?.focus(), 0);
    } catch (e) {
      console.warn("[HoyDesk] clipboard.readText failed:", e);
      setSaveError("No se pudo leer el portapapeles.");
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setPasteBusy(false);
    }
  }, [locked, pasteBusy]);

  const handleAudioDragOver = useCallback(
    (e) => {
      if (locked || isAudioLoading) {
        setIsAudioDragOver(false);
        return;
      }
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (!file) {
        setIsAudioDragOver(false);
        return;
      }
      setIsAudioDragOver(!validateVoiceFile(file));
    },
    [locked, isAudioLoading],
  );

  const handleAudioDragLeave = useCallback(() => {
    setIsAudioDragOver(false);
  }, []);

  const handleAudioDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsAudioDragOver(false);
      if (locked || isAudioLoading) return;

      const file = e.dataTransfer?.files?.[0];
      if (!file) return;

      const validationError = validateVoiceFile(file);
      if (validationError) return; // Ignore non-audio drops

      try {
        setIsAudioLoading(true);
        setSaveError(null);

        try {
          audioAbortRef.current?.abort();
        } catch (_err) {}

        const controller = new AbortController();
        audioAbortRef.current = controller;

        const { transcript } = await transcribeAudioFile(file, {
          signal: controller.signal,
        });
        setInput(transcript || "");
        window.setTimeout(() => textareaRef.current?.focus(), 0);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.warn("[HoyDesk] audio transcribe failed:", err);
        setSaveError("No se pudo transcribir el audio.");
        setTimeout(() => setSaveError(null), 2500);
      } finally {
        setIsAudioLoading(false);
      }
    },
    [locked, isAudioLoading],
  );

  useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setHelpOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [helpOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_PROMPT_KEY, agentPrompt);
    } catch (_err) {}
  }, [agentPrompt]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_PANEL_KEY, agentPanelOpen ? "1" : "0");
    } catch (_err) {}
  }, [agentPanelOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_AUTO_KEY, agentAutoMode ? "1" : "0");
    } catch (_err) {}
  }, [agentAutoMode]);

  useEffect(() => () => {
    if (robotClickRef.current.singleTimer) {
      clearTimeout(robotClickRef.current.singleTimer);
    }
  }, []);

  useEffect(() => {
    saveCodeProjectId(activeCodeProject);
  }, [activeCodeProject]);

  useEffect(() => {
    if (activeCodeProject && !codeProjects.some((p) => p.id === activeCodeProject)) {
      setActiveCodeProject(null);
      saveCodeProjectId(null);
    }
  }, [activeCodeProject, codeProjects]);

  const categoryPaths = useMemo(() => resolveCategoryPaths(settings), [settings]);
  const notebook = useMemo(() => getDailyLogNotebook(workspace, settings), [workspace, settings]);

  const placePath = useMemo(() => {
    if (!workspace || !settings?.dailyLogPath) return "Diario › Hoy › Registro";
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    const desk = workspace.desks?.find((d) => d.id === deskId);
    const drawer = desk && toSortedArray(desk.drawers).find((dr) => dr.id === drawerId);
    const nb = drawer && toSortedArray(drawer.notebooks).find((n) => n.id === notebookId);
    if (!desk || !drawer || !nb) return "Diario › Hoy › Registro";
    return `${desk.name} › ${drawer.name} › ${nb.name}`;
  }, [workspace, settings]);

  const captureDestination = useMemo(() => {
    if (activeCategory === "código" && activeCodeProject) {
      return `Temas › Código › ${getCodeProject(activeCodeProject, settings)?.notebookName || "Notas"}`;
    }
    if (activeCategory) {
      return categoryPathLabel(categoryPaths[activeCategory], workspace, toSortedArray);
    }
    if (activeCodeProject) {
      return `Temas › Código › ${getCodeProject(activeCodeProject, settings)?.notebookName}`;
    }
    return placePath;
  }, [activeCategory, activeCodeProject, categoryPaths, workspace, settings, placePath]);

  const captureOptsActive = asTask || activeGlyph || bujoOpen;

  const allTexts = useMemo(() => (notebook ? toSortedArray(notebook.texts) : []), [notebook]);
  const todayTexts = useMemo(() => allTexts.filter((t) => isToday(t.updatedAt || t.order)), [allTexts]);

  const sparkNote = useMemo(() => todayTexts.find((t) => isSparkNote(t.title)), [todayTexts]);
  const sparks = useMemo(() => parseSparks(sparkNote?.content).reverse(), [sparkNote]);

  const diarioNote = useMemo(() => todayTexts.find((t) => isDiarioNote(t.title)), [todayTexts]);
  const diarioEntries = useMemo(() => parseDailyLog(diarioNote?.content), [diarioNote]);
  const diarioFocus = useMemo(() => diarioEntries.filter((e) => e.type === "focus"), [diarioEntries]);
  const diarioEvents = useMemo(() => diarioEntries.filter((e) => e.type === "event"), [diarioEntries]);
  const diarioNotes = useMemo(() => diarioEntries.filter((e) => e.type === "note"), [diarioEntries]);
  const diarioTasks = useMemo(() => diarioEntries.filter((e) => e.type === "task" && !e.done), [diarioEntries]);
  const diarioMigrated = useMemo(() => diarioEntries.filter((e) => e.type === "migrated"), [diarioEntries]);

  const tasks = useMemo(() => {
    const list = [];
    todayTexts.forEach((text) => {
      if (isSparkNote(text.title) || isDiarioNote(text.title)) return;
      parseTasks(text.content).forEach((task) => {
        list.push({
          ...task,
          textId: text.id,
          slug: text.slug,
          dateSlug: text.dateSlug,
          dragId: `${text.id}-${task.lineIndex}`,
        });
      });
    });
    return list;
  }, [todayTexts]);

  const [orderedTasks, setOrderedTasks] = useState([]);
  useEffect(() => {
    setOrderedTasks(tasks);
  }, [tasks]);

  const undoneTasks = useMemo(() => orderedTasks.filter((t) => !t.done), [orderedTasks]);
  const doneTasks = useMemo(() => orderedTasks.filter((t) => t.done), [orderedTasks]);
  const nextActions = useMemo(() => undoneTasks.slice(0, 3), [undoneTasks]);
  const backlogTasks = useMemo(() => undoneTasks.slice(3), [undoneTasks]);
  const draggableUndone = backlogOpen ? undoneTasks : nextActions;

  const canDragReorder = useMemo(
    () =>
      draggableUndone.length > 0 &&
      draggableUndone.every((t, _i, arr) => arr.filter((x) => x.textId === t.textId).length === 1),
    [draggableUndone]
  );

  const notes = useMemo(() => {
    return todayTexts.filter((t) => {
      if (isSparkNote(t.title)) return false;
      return parseTasks(t.content).length === 0;
    });
  }, [todayTexts]);

  const attentionMessage = useMemo(() => {
    if (nextActions.length > 0) return `Próxima: ${nextActions[0].text}`;
    if (undoneTasks.length === 0 && doneTasks.length > 0) return "Tareas completadas. ¿Qué sigue?";
    if (undoneTasks.length === 0) return "Todo al día. Capturá lo que aparezca.";
    return `Tenés ${undoneTasks.length} tareas pendientes.`;
  }, [nextActions, undoneTasks, doneTasks]);

  const dateLabel = new Date().toLocaleDateString("es-AR", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (locked) return;
    const detectedEarly = detectEntryType(input);
    const isDump =
      isPasteDump(input) && !asTask && !detectedEarly && !activeGlyph;
    const trimmed = smartPaste(input);
    if (!trimmed || saving) return;
    setSaveError(null);
    setSaveReceipt(null);
    setSaving(true);
    try {
      let receipt = null;
      const detected = detectEntryType(trimmed);

      if (activeCategory) {
        let bujoLine = null;
        if (detected || activeGlyph) {
          bujoLine = detected ? trimmed : `${TYPE_TO_SYM[activeGlyph]} ${trimmed}`;
        }
        const codeOpts = activeCategory === "código" ? codeCaptureOpts(activeCodeProject, settings) : {};
        receipt = await captureToCategory(activeCategory, trimmed, { asTask, bujoLine, settings, ...codeOpts });
      } else if (isDump) {
        receipt = await captureToCategory("código", trimmed, { settings, ...codeCaptureOpts(activeCodeProject, settings) });
      } else if (detected || activeGlyph) {
        const line = detected ? trimmed : `${TYPE_TO_SYM[activeGlyph]} ${trimmed}`;
        receipt = await appendDailyLogEntry(line);
      } else if (asTask) {
        receipt = await quickAddTask(trimmed);
      } else {
        receipt = await appendDailySpark(trimmed);
      }
      if (!receipt) throw new Error("No se guardó. Intentá de nuevo.");
      if (activeCategory === "código" && activeCodeProject) touchRecentCodeProject(activeCodeProject);
      setSaveReceipt(receipt);
      setInput("");
      setActiveGlyph(null);
      setTimeout(() => receiptRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);

      // Auto polish → reading queue (non-blocking)
      if (receipt.needsPolish && receipt.textId) {
        setSaveReceipt((r) => (r?.textId === receipt.textId ? { ...r, polishStatus: "Pulido…" } : r));
        polishNoteAfterSave({
          deskId: receipt.deskId,
          drawerId: receipt.drawerId,
          notebookId: receipt.notebookId,
          textId: receipt.textId,
          content: receipt.content,
          title: receipt.title,
        }).then((p) => {
          if (!p) {
            setSaveReceipt((r) =>
              r?.textId === receipt.textId ? { ...r, polishStatus: null } : r
            );
            return;
          }
          setSaveReceipt((r) =>
            r?.textId === receipt.textId
              ? {
                  ...r,
                  title: p.title,
                  page: p.title,
                  content: p.content,
                  preview: (p.content || "").split("\n")[0],
                  polishStatus: "Listo",
                }
              : r
          );
        });
      }
    } catch (err) {
      console.error("[Today] capture failed:", err);
      setSaveError(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const openLongCapture = () => {
    if (locked) return;
    window.dispatchEvent(new CustomEvent("open-quick-capture"));
  };

  const handleDragEnd = async (result) => {
    if (locked) return;
    if (!result.destination || !settings?.dailyLogPath) return;
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    const undone = Array.from(undoneTasks);
    const [moved] = undone.splice(result.source.index, 1);
    undone.splice(result.destination.index, 0, moved);
    const next = [...undone, ...doneTasks];
    setOrderedTasks(next);
    try {
      const nb = getDailyLogNotebook(workspace, settings);
      const allIds = toSortedArray(nb?.texts || []).map((t) => t.id);
      const undoneIds = undone.map((t) => t.textId);
      const rest = allIds.filter((id) => !undoneIds.includes(id));
      await reorderTexts(deskId, drawerId, notebookId, [...undoneIds, ...rest]);
    } catch (err) {
      console.error("[Today] reorder failed:", err);
      setOrderedTasks(tasks);
    }
  };

  const handleToggleTask = async (task) => {
    if (locked) return;
    if (!settings?.dailyLogPath) return;
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    try {
      await toggleTaskLine(deskId, drawerId, notebookId, task.textId, task.lineIndex);
    } catch (err) {
      console.error("[Today] toggle task failed:", err);
    }
  };

  const handleAnyDragEnd = async (result) => {
    if (locked) return;
    if (!result.destination) return;
    if (result.source.droppableId === "diario-tasks") {
      if (!diarioNote || !settings?.dailyLogPath) return;
      const { deskId, drawerId, notebookId } = settings.dailyLogPath;
      const from = diarioTasks[result.source.index]?.lineIndex;
      const to = diarioTasks[result.destination.index]?.lineIndex;
      if (from == null || to == null) return;
      try {
        await reorderLineInText(deskId, drawerId, notebookId, diarioNote.id, from, to);
      } catch (err) {
        console.error("[Today] diario reorder failed:", err);
      }
      return;
    }
    if (result.source.droppableId === "today-tasks") {
      await handleDragEnd(result);
    }
  };

  const handleMigrate = async (entry, e) => {
    if (locked) return;
    e.stopPropagation();
    if (!diarioNote || !settings?.dailyLogPath) return;
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    try {
      await migrateDiarioLine(deskId, drawerId, notebookId, diarioNote.id, entry.lineIndex);
    } catch (err) {
      console.error("[Today] migrate failed:", err);
    }
  };

  const handleToggleDiarioTask = async (entry) => {
    if (locked) return;
    if (!diarioNote || !settings?.dailyLogPath) return;
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    try {
      await toggleTaskLine(deskId, drawerId, notebookId, diarioNote.id, entry.lineIndex);
    } catch (err) {
      console.error("[Today] diario toggle failed:", err);
    }
  };

  const openNote = (note) => {
    if (!note) return;
    const desk = workspace?.desks?.find((d) => d.id === (note.deskId || settings?.dailyLogPath?.deskId));
    const drawer = desk && toSortedArray(desk.drawers).find(
      (dr) => dr.id === (note.drawerId || settings?.dailyLogPath?.drawerId)
    );
    const nb = drawer && toSortedArray(drawer.notebooks).find(
      (n) => n.id === (note.notebookId || settings?.dailyLogPath?.notebookId)
    );
    const enriched = {
      ...note,
      id: note.id || note.textId,
      deskSlug: note.deskSlug || desk?.slug,
      drawerSlug: note.drawerSlug || drawer?.slug,
      notebookSlug: note.notebookSlug || nb?.slug,
      deskId: note.deskId || settings?.dailyLogPath?.deskId,
      drawerId: note.drawerId || settings?.dailyLogPath?.drawerId,
      notebookId: note.notebookId || settings?.dailyLogPath?.notebookId,
    };
    const found = findTextInWorkspace(workspace, enriched) || enriched;
    const url = textReaderPath(found);
    if (url && url !== HOME_PATH) {
      navigate(url);
      return;
    }
    if (!navigateToText(navigate, found)) {
      setSaveError("No se pudo abrir la nota.");
    }
  };

  const openFromReceipt = (receipt) => {
    if (!receipt) return;
    // Prefer the URL baked into the save receipt — do not wait on workspace sync.
    const url =
      (receipt.url && receipt.url !== HOME_PATH && receipt.url) ||
      textReaderPath({
        id: receipt.textId || receipt.id,
        textId: receipt.textId || receipt.id,
        slug: receipt.slug,
        dateSlug: receipt.dateSlug,
        deskSlug: receipt.deskSlug,
        drawerSlug: receipt.drawerSlug,
        notebookSlug: receipt.notebookSlug,
      });
    if (url && url !== HOME_PATH) {
      navigate(url);
      return;
    }
    setSaveError("No se pudo abrir la nota.");
  };

  const handleDeleteNote = async (textId, e) => {
    e?.stopPropagation();
    console.log("[HoyDesk] delete click", {
      textId,
      locked,
      dailyLogPath: settings?.dailyLogPath ?? null,
    });
    if (locked) {
      console.warn("[HoyDesk] delete blocked — edit lock (tap logo 5× to unlock)");
      return;
    }
    if (!settings?.dailyLogPath) {
      console.warn("[HoyDesk] delete blocked — no dailyLogPath in settings");
      return;
    }
    if (!(await confirmInApp({
      title: "Eliminar nota",
      message: "¿Eliminar esta nota?",
      confirmLabel: "Eliminar",
      danger: true,
    }))) {
      console.log("[HoyDesk] delete cancelled by user", { textId });
      return;
    }
    const { deskId, drawerId, notebookId } = settings.dailyLogPath;
    console.log("[HoyDesk] delete confirmed", { textId, deskId, drawerId, notebookId });
    await runFeedbackFlow({
      actionId: `delete-note:${textId}`,
      label: "Eliminar",
      dbStep: async () => {
        await deleteText(deskId, drawerId, notebookId, textId);
      },
      successMessage: "Eliminado",
      errorMessage: "No se pudo eliminar",
    })
      .then(() => console.log("[HoyDesk] delete ok", { textId }))
      .catch((err) => {
        console.error("[HoyDesk] delete failed", { textId, error: err });
      });
  };

  const renderTaskRow = (task, dragProvided, snapshot, showDrag) => (
    <div
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      style={{
        ...styles.glassListItem,
        background: snapshot.isDragging ? "rgba(212,175,55,0.12)" : styles.glassListItem.background,
        ...dragProvided.draggableProps.style,
      }}
    >
      {showDrag && !locked && canDragReorder && (
        <div {...dragProvided.dragHandleProps} style={{ color: "rgba(255,255,255,0.4)", cursor: "grab", touchAction: "none" }}>
          <GripVertical size={18} />
        </div>
      )}
      <button
        type="button"
        disabled={locked}
        onClick={() => {
          if (locked) return;
          handleToggleTask(task);
        }}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: locked ? "not-allowed" : "pointer",
          opacity: locked ? 0.4 : 1,
        }}
      >
        {task.done ? <CheckSquare size={18} color="#4ade80" /> : <Square size={18} color={ACCENT} />}
      </button>
      <span
        onClick={() => openNote(task)}
        style={{
          color: task.done ? "rgba(255,255,255,0.4)" : "#fff",
          textDecoration: task.done ? "line-through" : "none",
          fontSize: "0.95rem",
          flex: 1,
          cursor: "pointer",
        }}
      >
        {task.text}
      </span>
    </div>
  );

  const diarioRowStyle = {
    ...styles.glassListItem,
    cursor: "default",
  };

  return (
    <>
    <div style={styles.page}>
      <BackgroundImage image={BACKGROUNDS.dashboard} />
      {workspaceLoading ? (
        <div style={{ ...styles.deskContent, ...styles.center, position: "relative", zIndex: 1, minHeight: "40vh" }}>
          Cargando…
        </div>
      ) : (
      <div style={{ ...styles.deskContent, position: "relative", zIndex: 1, paddingTop: "12px", paddingBottom: "100px" }}>
        <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
          <div>
            <h1 className="dashboard-greeting" style={{ ...styles.greeting, marginTop: 0, fontSize: isMobile ? "1.25rem" : "1.5rem" }}>
              <Eye size={24} style={{ verticalAlign: "middle", marginRight: "10px", opacity: 0.8 }} />
              ¿En qué deberíamos enfocarnos hoy?
            </h1>
            <p style={{ ...styles.subtext, marginTop: 6, fontSize: "0.95rem", color: "rgba(255,255,255,0.75)" }}>
              {attentionMessage}
            </p>
            <p style={{ ...styles.subtext, textTransform: "capitalize", marginTop: 4 }}>{dateLabel}</p>
          </div>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label="Ayuda de Hoy"
            style={styles.viewToggle}
          >
            <HelpCircle size={18} />
          </button>
        </header>

        {!locked && (
          <HoyCategoryBar
            workspace={workspace}
            settings={settings}
            categoryPaths={categoryPaths}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            activeCodeProject={activeCodeProject}
            setActiveCodeProject={setActiveCodeProject}
            onEditProjects={() => setCodeEditorOpen(true)}
            viewToggleStyle={styles.viewToggle}
            viewToggleActiveStyle={styles.viewToggleActive}
          />
        )}

        <GlassSection
          title="Captura"
          icon={PenLine}
          headerExtra={(
            <span
              style={{
                ...styles.subtext,
                fontSize: "0.75rem",
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
              title={captureDestination}
            >
              📍 {captureDestination}
            </span>
          )}
        >
          {locked ? (
            <UnlockSeal size={isMobile ? 76 : 96} />
          ) : (
          <>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {agentPanelOpen && (
                <textarea
                  ref={agentPromptRef}
                  className="hoy-capture-agent-ph"
                  style={{
                    ...styles.glassTextarea,
                    minHeight: isMobile ? "40px" : "56px",
                    fontSize: "0.85rem",
                    color: "rgba(255,255,255,0.85)",
                  }}
                  rows={2}
                  value={agentPrompt}
                  onChange={(e) => setAgentPrompt(e.target.value)}
                  placeholder={agentFieldPlaceholder}
                  aria-label="Instrucciones para agente"
                />
              )}
              <textarea
                className={agentPanelOpen ? "hoy-capture-agent-ph" : undefined}
                style={{
                  ...styles.glassTextarea,
                  minHeight: isMobile ? "48px" : "72px",
                  outline: isAudioDragOver ? "2px dashed rgba(212, 175, 55, 0.7)" : undefined,
                  outlineOffset: isAudioDragOver ? "2px" : undefined,
                }}
                rows={2}
                value={input}
                onChange={(e) => { setInput(e.target.value); setSaveError(null); }}
                ref={textareaRef}
                onDragOver={handleAudioDragOver}
                onDragLeave={handleAudioDragLeave}
                onDrop={handleAudioDrop}
                onPaste={(e) => {
                  const pasted = e.clipboardData?.getData("text/plain");
                  if (!pasted) return;
                  e.preventDefault();
                  const clean = smartPaste(pasted);
                  const el = e.target;
                  const start = el.selectionStart ?? input.length;
                  const end = el.selectionEnd ?? input.length;
                  const merged = input.slice(0, start) + clean + input.slice(end);
                  setInput(smartPaste(merged));
                  setSaveError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder={mainCapturePlaceholder}
                enterKeyHint="done"
                autoFocus
              />

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  onClick={handlePasteButton}
                  disabled={locked || pasteBusy}
                  style={{
                    ...styles.glassActionBtn,
                    flex: 1,
                    height: "44px",
                    opacity: locked || pasteBusy ? 0.6 : 1,
                    cursor: locked || pasteBusy ? "not-allowed" : "pointer",
                    padding: "0 10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "flex-start",
                    gap: "8px",
                  }}
                  title="Pegar del portapapeles"
                  aria-label="Pegar del portapapeles"
                >
                  {pasteBusy ? <Loader2 size={18} className="animate-spin" /> : <ClipboardPaste size={20} />}
                  {!isMobile && <span>Pegar</span>}
                </button>

                <button
                  type="button"
                  onClick={handleCopyCapture}
                  disabled={locked || !captureCopyText}
                  style={{
                    ...styles.glassActionBtn,
                    width: "44px",
                    height: "44px",
                    opacity: locked || !captureCopyText ? 0.6 : 1,
                    cursor: locked || !captureCopyText ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title={agentAutoMode ? "Copiar y limpiar captura" : "Copiar captura (+ instrucciones agente)"}
                  aria-label="Copiar captura"
                >
                  <ClipboardCopy size={20} />
                </button>

                <button
                  type="button"
                  onClick={handleRobotModeClick}
                  disabled={locked}
                  style={{
                    ...styles.glassActionBtn,
                    width: "44px",
                    height: "44px",
                    opacity: locked ? 0.6 : 1,
                    cursor: locked ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...(agentPanelOpen || agentAutoMode ? styles.viewToggleActive : {}),
                    ...(agentAutoMode ? { boxShadow: `0 0 0 1px ${ACCENT}` } : {}),
                  }}
                  title={robotModeTitle}
                  aria-label={robotModeTitle}
                  aria-pressed={agentPanelOpen}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 1, fontSize: "0.7rem", lineHeight: 1, color: agentAutoMode ? ACCENT : "inherit" }}>
                    <span>[</span>
                    <AgentBotIcon size={14} auto={agentAutoMode} />
                    <span>]</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setCaptureOptsOpen((o) => !o)}
                  disabled={locked}
                  style={{
                    ...styles.glassActionBtn,
                    width: "44px",
                    height: "44px",
                    opacity: locked ? 0.6 : 1,
                    cursor: locked ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...(captureOptsOpen || captureOptsActive ? styles.viewToggleActive : {}),
                  }}
                  title="Opciones: tarea, BuJo, captura larga"
                  aria-label="Opciones de captura"
                  aria-expanded={captureOptsOpen}
                >
                  <ListFilter size={18} />
                </button>

                <button
                  type="button"
                  onClick={openCaptureFullscreen}
                  disabled={locked}
                  style={{
                    ...styles.glassActionBtn,
                    width: "44px",
                    height: "44px",
                    opacity: locked ? 0.6 : 1,
                    cursor: locked ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Expandir editor"
                  aria-label="Expandir editor"
                >
                  <Maximize2 size={20} />
                </button>

                <button
                  type="submit"
                  disabled={locked || saving || !input.trim()}
                  style={{
                    ...styles.glassActionBtn,
                    flex: 1,
                    height: "44px",
                    opacity: locked || saving || !input.trim() ? 0.6 : 1,
                    cursor: locked || saving || !input.trim() ? "not-allowed" : "pointer",
                    padding: "0 10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "flex-start",
                    gap: "8px",
                  }}
                  title="Guardar (Enter también)"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} />}
                  {!isMobile && <span>Guardar</span>}
                </button>
              </div>

              {captureOptsOpen && (
                <div
                  style={{
                    ...styles.glassCard,
                    marginTop: "8px",
                    padding: "10px 12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <label style={{ ...styles.subtext, display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }}>
                    <input type="checkbox" checked={asTask} disabled={locked} onChange={(e) => setAsTask(e.target.checked)} />
                    <span><strong style={{ color: "#fff" }}>Tarea</strong> — guarda como <code>[ ]</code> en Siguiente</span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => setBujoOpen((o) => !o)}
                      style={{ ...styles.viewToggle, ...(bujoOpen ? styles.viewToggleActive : {}), ...(locked ? { opacity: 0.45, cursor: "not-allowed" } : {}) }}
                      disabled={locked}
                    >
                      {bujoOpen ? "▾ BuJo" : "▸ BuJo"}
                    </button>
                    <button
                      type="button"
                      onClick={openLongCapture}
                      style={{ ...styles.viewToggle, ...(locked ? { opacity: 0.45, cursor: "not-allowed" } : {}) }}
                      disabled={locked}
                      title="Captura larga en ventana aparte"
                    >
                      Captura larga · Cmd+Shift+N
                    </button>
                  </div>
                  {bujoOpen && !locked && <SymbolStrip active={activeGlyph} onPick={setActiveGlyph} />}
                </div>
              )}
            </div>

            {saving && !saveReceipt && (
              <div style={{ ...styles.subtext, marginTop: "12px" }} role="status">Guardando…</div>
            )}
            {saveError && (
              <div style={{ color: "#ffb4b4", marginTop: "12px", fontSize: "0.85rem" }} role="alert">✗ {saveError}</div>
            )}
            {saveReceipt && (
              <div ref={receiptRef} style={{ ...styles.glassCard, marginTop: "12px", cursor: "default" }} role="status">
                <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: "6px", fontSize: "0.85rem" }}>✓ Guardado</div>
                <div style={styles.subtext}><strong style={{ color: "#fff" }}>Dónde:</strong> {saveReceipt.path} › {saveReceipt.page}</div>
                <div style={{ ...styles.subtext, marginTop: "4px" }}><strong style={{ color: "#fff" }}>Sección:</strong> {saveReceipt.section}</div>
                {saveReceipt.polishStatus && (
                  <div style={{ ...styles.subtext, marginTop: "4px", color: "rgba(212,175,55,0.9)" }}>
                    {saveReceipt.polishStatus}
                  </div>
                )}
                {saveReceipt.preview && <div style={{ ...styles.subtext, marginTop: "4px", fontStyle: "italic" }}>«{saveReceipt.preview}»</div>}
                {saveReceipt.canQuickMove !== false && saveReceipt.textId && (
                  <QuickMoveBar
                    location={saveReceipt}
                    settings={settings}
                    onMoved={(receipt) => setSaveReceipt(receipt)}
                  />
                )}
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button type="button" onClick={() => openFromReceipt(saveReceipt)} style={{ ...styles.viewToggle, ...styles.viewToggleActive }}>
                    Leer más
                  </button>
                  <button type="button" onClick={() => setSaveReceipt(null)} style={{ ...styles.viewToggle, border: "none", background: "transparent" }}>
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </form>
          </>
          )}
        </GlassSection>

        <ReadingQueueBanner />

        <DragDropContext onDragEnd={handleAnyDragEnd}>
          {diarioNote && (
            <div style={{ textAlign: "right", marginBottom: "8px" }}>
              <button type="button" onClick={() => openNote(diarioNote)} style={{ ...styles.viewToggle, border: "none", background: "transparent", color: ACCENT, fontSize: "0.75rem" }}>
                <BookOpen size={14} /> Abrir página del diario
              </button>
            </div>
          )}

          {undoneTasks.length > 0 && (
            <GlassSection title={`Siguiente · ${Math.min(3, undoneTasks.length)} de ${undoneTasks.length}`} icon={CheckSquare}>
              <SafeDroppable droppableId="today-tasks" isDropDisabled={locked}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {draggableUndone.map((task, idx) => (
                      <Draggable
                        key={task.dragId}
                        draggableId={task.dragId}
                        index={idx}
                        isDragDisabled={locked || !canDragReorder}
                      >
                        {(dragProvided, snapshot) => renderTaskRow(task, dragProvided, snapshot, true)}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </SafeDroppable>
              {backlogTasks.length > 0 && (
                <button
                  type="button"
                  onClick={() => setBacklogOpen((o) => !o)}
                  style={{ ...styles.viewToggle, marginTop: "12px", width: "100%", justifyContent: "center" }}
                >
                  {backlogOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {backlogOpen ? "Ocultar" : `Más tareas (${backlogTasks.length})`}
                </button>
              )}
              {!locked && canDragReorder && (
                <p style={{ ...styles.subtext, margin: "8px 0 0", fontSize: "0.75rem" }}>Arrastrá ⋮⋮ para reordenar</p>
              )}
            </GlassSection>
          )}

          {doneTasks.length > 0 && (
            <GlassSection title={`Completadas (${doneTasks.length})`}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", opacity: 0.6 }}>
                {doneTasks.map((task) => (
                  <div key={task.dragId} style={styles.glassListItem}>
                    <CheckSquare size={18} color="#4ade80" />
                    <span style={{ textDecoration: "line-through", flex: 1, fontSize: "0.95rem" }}>{task.text}</span>
                  </div>
                ))}
              </div>
            </GlassSection>
          )}

          {diarioFocus.length > 0 && (
            <GlassSection title="Prioridad">
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {diarioFocus.map((e, i) => (
                  <div key={i} style={{ ...diarioRowStyle, borderLeft: `3px solid ${ACCENT}` }}>
                    <EntryIcon type="focus" />
                    <span style={{ flex: 1 }}>{e.text}</span>
                  </div>
                ))}
              </div>
            </GlassSection>
          )}

          {diarioEvents.length > 0 && (
            <GlassSection title="Eventos">
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {diarioEvents.map((e, i) => (
                  <div key={i} style={diarioRowStyle}>
                    <EntryIcon type="event" />
                    <span>{e.text}</span>
                  </div>
                ))}
              </div>
            </GlassSection>
          )}

          {diarioTasks.length > 0 && (
            <GlassSection title="Diario · arrastrá ⋮⋮">
              <SafeDroppable droppableId="diario-tasks" isDropDisabled={locked}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {diarioTasks.map((e, i) => (
                      <Draggable key={`d-${e.lineIndex}`} draggableId={`d-${e.lineIndex}`} index={i} isDragDisabled={locked}>
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...diarioRowStyle,
                              background: snapshot.isDragging ? "rgba(212,175,55,0.12)" : diarioRowStyle.background,
                              ...dragProvided.draggableProps.style,
                            }}
                          >
                            <HintTip label="Mover" hint="Arrastrá para cambiar el orden">
                              <div
                                {...dragProvided.dragHandleProps}
                                style={{
                                  color: "rgba(255,255,255,0.4)",
                                  cursor: locked ? "not-allowed" : "grab",
                                  touchAction: "none",
                                  opacity: locked ? 0.35 : 1,
                                }}
                              >
                                <GripVertical size={18} />
                              </div>
                            </HintTip>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => {
                                if (locked) return;
                                handleToggleDiarioTask(e);
                              }}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: locked ? "not-allowed" : "pointer",
                                opacity: locked ? 0.4 : 1,
                              }}
                            >
                              <Square size={18} color={ACCENT} />
                            </button>
                            <span
                              style={{ flex: 1, cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.6 : 1 }}
                              onClick={() => {
                                if (locked) return;
                                handleToggleDiarioTask(e);
                              }}
                            >
                              {e.text}
                            </span>
                            <HintTip label="Migrar" hint="Pasar a otro día (BuJo ›)">
                              <button
                                type="button"
                                disabled={locked}
                                onClick={(ev) => {
                                  if (locked) return;
                                  handleMigrate(e, ev);
                                }}
                                aria-label="Migrar"
                                style={{
                                  ...styles.viewToggle,
                                  opacity: locked ? 0.4 : 1,
                                  cursor: locked ? "not-allowed" : "pointer",
                                  filter: locked ? "grayscale(1)" : "none",
                                }}
                              >
                                <GlyphMigrate />
                              </button>
                            </HintTip>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </SafeDroppable>
            </GlassSection>
          )}

          {diarioMigrated.length > 0 && (
            <GlassSection title="Migrado" style={{ opacity: 0.55 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {diarioMigrated.map((e, i) => (
                  <div key={i} style={diarioRowStyle}>
                    <EntryIcon type="migrated" />
                    <span style={{ textDecoration: "line-through" }}>{e.text}</span>
                  </div>
                ))}
              </div>
            </GlassSection>
          )}

          {sparks.length > 0 && (
            <GlassSection
              title={`Ideas de hoy (${sparks.length})`}
              icon={Sparkles}
            >
              {sparkNote && (
                <button type="button" onClick={() => openNote(sparkNote)} style={{ ...styles.viewToggle, alignSelf: "flex-end", marginBottom: "10px", fontSize: "0.75rem", border: "none", background: "transparent", color: ACCENT }}>
                  Leer todo el hilo
                </button>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {sparks.slice(0, 6).map((spark, i) => (
                  <div
                    key={i}
                    onClick={() => sparkNote && openNote(sparkNote)}
                    style={{
                      ...styles.glassListItem,
                      borderLeft: `3px solid rgba(212,175,55,0.4)`,
                    }}
                  >
                    {spark}
                  </div>
                ))}
                {sparks.length > 6 && (
                  <button type="button" onClick={() => sparkNote && openNote(sparkNote)} style={{ ...styles.viewToggle, border: "none", background: "transparent", color: "rgba(255,255,255,0.5)", justifyContent: "flex-start" }}>
                    + {sparks.length - 6} más en el hilo…
                  </button>
                )}
              </div>
            </GlassSection>
          )}
        </DragDropContext>

        {notes.length > 0 && (
          <GlassSection title="Notas sueltas">
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {notes.map((note) => (
                <div key={note.id} onClick={() => openNote(note)} style={{ ...styles.glassCard, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: ACCENT, fontSize: "0.75rem", fontWeight: "bold", marginBottom: "4px" }}>{note.title}</div>
                      <div style={styles.subtext}>{plainPreview(note.content)}</div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openNote(note);
                        }}
                        style={{ ...styles.viewToggle, ...styles.viewToggleActive, marginTop: "10px", fontSize: "0.75rem" }}
                      >
                        Leer más
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={locked}
                      onClick={(e) => {
                        console.log("[HoyDesk] trash button", { noteId: note.id, title: note.title, locked });
                        if (locked) return;
                        handleDeleteNote(note.id, e);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "rgba(255,255,255,0.4)",
                        cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.35 : 1,
                        filter: locked ? "grayscale(1)" : "none",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </GlassSection>
        )}

        {diarioNotes.length > 0 && (
          <GlassSection title="Notas del diario">
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {diarioNotes.map((e, i) => (
                <div key={i} style={diarioRowStyle}>{e.text}</div>
              ))}
            </div>
          </GlassSection>
        )}

        {undoneTasks.length === 0 && sparks.length === 0 && notes.length === 0 && diarioEntries.length === 0 && (
          <GlassSection>
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Sparkles size={40} style={{ marginBottom: "12px", color: ACCENT, opacity: 0.8 }} />
              <p style={{ fontSize: "1rem", color: "#fff", fontWeight: 600, margin: "0 0 8px" }}>Nada anotado todavía</p>
              <p style={{ ...styles.subtext, margin: 0, lineHeight: 1.55 }}>
                Escribí arriba, soltá o usá <strong style={{ color: ACCENT }}>Pegar</strong>. Se guarda en <strong style={{ color: ACCENT }}>{placePath}</strong>.
              </p>
            </div>
          </GlassSection>
        )}
      </div>
      )}

      {captureFullscreenVisible && (
        <div
          role="dialog"
          aria-label="Captura en pantalla completa"
          style={styles.modalOverlay}
          onClick={() => closeCaptureFullscreen()}
        >
          <div
            style={{
              ...styles.modalPanel,
              maxWidth: "980px",
              height: "100%",
              borderRadius: 0,
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              opacity: captureFullscreenAnim ? 1 : 0,
              transform: captureFullscreenAnim ? "translateY(0) scale(1)" : "translateY(10px) scale(0.98)",
              transition: "opacity 180ms ease, transform 180ms ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PenLine size={18} color="#fff" />
                  <strong style={{ color: ACCENT }}>Captura</strong>
                </div>
                {captureInterim && (
                  <span style={{ ...styles.subtext, color: "rgba(255,255,255,0.6)" }}>
                    {captureInterim}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <DictationLangToggle
                  value={dictationLang}
                  disabled={locked}
                  onChange={(lang) => {
                    if (locked) return;
                    setDictationLang(lang);
                    if (isCaptureRecording) {
                      stopCaptureRecording();
                      setTimeout(startCaptureRecording, 120);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => (isCaptureRecording ? stopCaptureRecording() : startCaptureRecording())}
                  disabled={locked}
                  style={{
                    ...styles.viewToggle,
                    padding: "8px 12px",
                    ...(isCaptureRecording ? styles.viewToggleActive : {}),
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  title={isCaptureRecording ? "Detener dictado" : "Dictar con micrófono"}
                >
                  {isCaptureRecording ? <MicOff size={16} /> : <Mic size={16} />}
                  <span style={{ fontSize: "0.85rem" }}>{isCaptureRecording ? "Escuchando…" : "Dictar"}</span>
                </button>

                <button
                  type="button"
                  onClick={handlePasteButton}
                  disabled={locked || pasteBusy}
                  style={{
                    ...styles.viewToggle,
                    padding: "8px 12px",
                    opacity: locked || pasteBusy ? 0.6 : 1,
                    cursor: locked || pasteBusy ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  title="Pegar del portapapeles"
                >
                  <ClipboardPaste size={16} />
                  <span style={{ fontSize: "0.85rem" }}>Pegar</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyCapture}
                  disabled={locked || !captureCopyText}
                  style={{
                    ...styles.viewToggle,
                    padding: "8px 12px",
                    opacity: locked || !captureCopyText ? 0.6 : 1,
                    cursor: locked || !captureCopyText ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  title={agentAutoMode ? "Copiar y limpiar captura" : "Copiar captura (+ instrucciones agente)"}
                  aria-label="Copiar captura"
                >
                  <ClipboardCopy size={16} />
                  <span style={{ fontSize: "0.85rem" }}>Copiar</span>
                </button>

                <button
                  type="button"
                  onClick={handleRobotModeClick}
                  disabled={locked}
                  style={{
                    ...styles.viewToggle,
                    padding: "8px 12px",
                    ...(agentPanelOpen || agentAutoMode ? styles.viewToggleActive : {}),
                    ...(agentAutoMode ? { boxShadow: `0 0 0 1px ${ACCENT}` } : {}),
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  title={robotModeTitle}
                  aria-label={robotModeTitle}
                  aria-pressed={agentPanelOpen}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 1, fontSize: "0.75rem", lineHeight: 1, color: agentAutoMode ? ACCENT : "inherit" }}>
                    <span>[</span>
                    <AgentBotIcon size={14} auto={agentAutoMode} />
                    <span>]</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => closeCaptureFullscreen()}
                  aria-label="Cerrar"
                  style={{
                    background: "none",
                    border: "none",
                    color: "rgba(255,255,255,0.7)",
                    cursor: "pointer",
                    padding: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div style={{ flex: 1, marginTop: 12, display: "flex", flexDirection: "column" }}>
              <form
                onSubmit={handleSubmit}
                style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}
              >
                {agentPanelOpen && (
                  <textarea
                    ref={agentPromptRef}
                    className="hoy-capture-agent-ph"
                    value={agentPrompt}
                    onChange={(e) => setAgentPrompt(e.target.value)}
                    placeholder={agentFieldPlaceholder}
                    aria-label="Instrucciones para agente"
                    style={{
                      ...styles.glassTextarea,
                      minHeight: "72px",
                      resize: "vertical",
                      fontSize: "0.9rem",
                    }}
                  />
                )}
                <div
                  style={{
                    flex: 1,
                    borderRadius: 16,
                    border: isAudioDragOver ? "1px dashed rgba(212, 175, 55, 0.8)" : "1px solid rgba(255,255,255,0.08)",
                    padding: 12,
                    background: "rgba(0,0,0,0.12)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onDragOver={handleAudioDragOver}
                  onDragLeave={handleAudioDragLeave}
                  onDrop={handleAudioDrop}
                >
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setSaveError(null); }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData?.getData("text/plain");
                      if (!pasted) return;
                      e.preventDefault();
                      const clean = smartPaste(pasted);
                      const el = e.target;
                      const start = el.selectionStart ?? input.length;
                      const end = el.selectionEnd ?? input.length;
                      const merged = input.slice(0, start) + clean + input.slice(end);
                      setInput(smartPaste(merged));
                      setSaveError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder={mainCapturePlaceholder}
                    enterKeyHint="done"
                    autoFocus
                    className={agentPanelOpen ? "hoy-capture-agent-ph" : undefined}
                    style={{
                      ...styles.glassTextarea,
                      flex: 1,
                      width: "100%",
                      resize: "none",
                      minHeight: 0,
                    }}
                    readOnly={locked}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ ...styles.subtext, color: saveError || captureVoiceError ? "#ffb4b4" : "rgba(255,255,255,0.6)" }}>
                    {saveError || captureVoiceError || (locked ? "Bloqueado" : "Enter guarda · Esc cierra")}
                  </div>

                  <button
                    type="submit"
                    disabled={locked || saving || !input.trim()}
                    style={{
                      ...styles.glassActionBtn,
                      padding: "10px 18px",
                      opacity: locked || saving || !input.trim() ? 0.6 : 1,
                      cursor: locked || saving || !input.trim() ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                    title="Guardar"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} />}
                    <span>Guardar</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {helpOpen && (
        <div role="dialog" aria-label="Ayuda de Hoy" style={styles.modalOverlay} onClick={() => setHelpOpen(false)}>
          <div style={{ ...styles.modalPanel, maxWidth: "520px", maxHeight: "85vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <strong style={{ color: ACCENT }}>Ayuda · Hoy</strong>
              <button type="button" onClick={() => setHelpOpen(false)} aria-label="Cerrar" style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
                <X size={18} />
              </button>
            </div>

            <HelpBlock title="Captura">
              <li>Enter o <strong style={{ color: "#fff" }}>Guardar</strong> escribe en la ruta mostrada arriba (📍).</li>
              <li><strong style={{ color: "#fff" }}>Categorías</strong> arriba — elegí carpeta antes de guardar (Código, Iglesia, Vida, Cuentos).</li>
              <li>Proyectos bajo Código — el chip activo elige el cuaderno destino.</li>
              <li>Pegar bloques largos (código, tablas, CLI) van a <strong style={{ color: "#fff" }}>Temas › Código › Notas</strong> salvo otra categoría activa.</li>
              <li>Botón <strong style={{ color: "#fff" }}>filtro</strong> en la fila de acciones — Tarea, BuJo, captura larga.</li>
              <li><strong style={{ color: "#fff" }}>[🤖]</strong> — instrucciones fijas para copiar al agente; doble clic = modo auto.</li>
            </HelpBlock>

            <HelpBlock title="Tareas y checkboxes">
              <li>Al capturar: activá <strong style={{ color: "#fff" }}>Tarea</strong> (opciones ▾) o escribí una línea con <code>[ ]</code>.</li>
              <li>En <strong style={{ color: "#fff" }}>Siguiente</strong>: tocá el cuadrado ☐ para marcar hecho ☑ (tachado abajo en Completadas).</li>
              <li>Solo ves las 3 primeras pendientes; el resto en <strong style={{ color: "#fff" }}>Más tareas</strong>.</li>
              <li>En <strong style={{ color: "#fff" }}>Diario · arrastrá</strong>: mismo checkbox en tareas embebidas en la página del diario.</li>
            </HelpBlock>

            <HelpBlock title="Reordenar">
              <li>Arrastrá el ícono <strong style={{ color: "#fff" }}>⋮⋮</strong> a la izquierda de cada tarea en Siguiente.</li>
              <li>Solo aparece si todas las tareas visibles vienen de la misma nota (si mezclás varias notas, reordená abriendo la nota).</li>
              <li>Diario: arrastrá ⋮⋮ en la sección «Diario · arrastrá» para cambiar orden de líneas.</li>
              <li>En desktop: click + arrastrar. En móvil: mantener presionado el ⋮⋮.</li>
            </HelpBlock>

            <HelpBlock title="BuJo y atajos">
              <li><strong style={{ color: "#fff" }}>BuJo</strong> — elegí símbolo (• ○ ★ !) antes de guardar; la línea entra al diario con ese tipo.</li>
              <li><strong style={{ color: "#fff" }}>Cmd/Ctrl+Shift+N</strong> — captura larga en modal global (desde cualquier pantalla).</li>
              <li>Tocá el texto de una tarea para abrirla y editarla.</li>
            </HelpBlock>
          </div>
        </div>
      )}
    </div>

      <CodeProjectsEditor
        isOpen={codeEditorOpen}
        onClose={() => setCodeEditorOpen(false)}
        settings={settings}
      />
    </>
  );
}
